import * as THREE from 'three';
import type { LevelDef } from '../levels/types';
import type { CarSnapshot, CarSpec, SimEvent } from '../sim/types';
import { FIXED_DT } from '../sim/types';
import { SimWorld } from '../sim/SimWorld';
import { initRapier } from '../physics/rapier';
import type { InputManager } from '../controls/InputManager';
import { buildScene, type BuiltScene } from './SceneBuilder';
import { CarVisual } from './CarVisual';
import { ChaseCamera, CockpitCamera } from './cameras';
import { PipView, PIP_RECTS } from './PipView';

export type CameraMode = 'chase' | 'cockpit';

export interface EngineOptions {
  canvas: HTMLCanvasElement;
  level: LevelDef;
  spec: CarSpec;
  inputManager: InputManager;
  cameraMode: CameraMode;
  /** Llamado en cada tick de simulación (evaluador, HUD, audio). */
  onTick: (snap: CarSnapshot, events: SimEvent[]) => void;
}

const MAX_STEPS_PER_FRAME = 5;

/**
 * Une simulación (paso fijo determinista) y render (frecuencia variable con
 * interpolación). La simulación jamás depende del framerate.
 */
export class EngineApp {
  paused = false;
  private cameraMode: CameraMode;
  private renderer: THREE.WebGLRenderer;
  private built: BuiltScene;
  private carVisual: CarVisual;
  private chase = new ChaseCamera();
  private cockpit = new CockpitCamera();
  private sim: SimWorld;
  private opts: EngineOptions;
  private raf = 0;
  private lastMs = 0;
  private accumulator = 0;
  private disposed = false;
  private lastSnap: CarSnapshot | null = null;
  private activeZoneId: string | null = null;
  private timeS = 0;

  private prevPos = new THREE.Vector3();
  private prevQuat = new THREE.Quaternion();
  private curPos = new THREE.Vector3();
  private curQuat = new THREE.Quaternion();
  private tmpPos = new THREE.Vector3();
  private tmpQuat = new THREE.Quaternion();
  private tmpOffset = new THREE.Vector3();
  private tmpQ2 = new THREE.Quaternion();

  private pipMirror: PipView;
  private pipRear: PipView;
  private pipReverse: PipView;

  private resizeObserver: ResizeObserver | null = null;

  static async create(opts: EngineOptions): Promise<EngineApp> {
    await initRapier();
    return new EngineApp(opts);
  }

  private constructor(opts: EngineOptions) {
    this.opts = opts;
    this.cameraMode = opts.cameraMode;
    this.renderer = new THREE.WebGLRenderer({ canvas: opts.canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    this.built = buildScene(opts.level);
    this.carVisual = new CarVisual(opts.spec);
    this.built.scene.add(this.carVisual.group);

    this.sim = new SimWorld({
      spec: opts.spec,
      environment: opts.level.environment,
      gripFactor: opts.level.weather.gripFactor,
      spawn: opts.level.spawn,
    });
    const snap0 = this.sim.snapshot();
    this.curPos.set(snap0.pos.x, snap0.pos.y, snap0.pos.z);
    this.curQuat.set(snap0.quat.x, snap0.quat.y, snap0.quat.z, snap0.quat.w);
    this.prevPos.copy(this.curPos);
    this.prevQuat.copy(this.curQuat);
    this.lastSnap = snap0;

    this.pipMirror = new PipView(30, 16 / 10);
    this.pipRear = new PipView(42, 16 / 8);
    this.pipReverse = new PipView(95, 16 / 9);

    this.handleResize();
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(opts.canvas.parentElement ?? opts.canvas);
  }

  private handleResize(): void {
    const parent = this.opts.canvas.parentElement;
    const w = parent?.clientWidth ?? window.innerWidth;
    const h = parent?.clientHeight ?? window.innerHeight;
    this.renderer.setSize(w, h, false);
    const aspect = w / h;
    this.chase.camera.aspect = aspect;
    this.chase.camera.updateProjectionMatrix();
    this.cockpit.camera.aspect = aspect;
    this.cockpit.camera.updateProjectionMatrix();
  }

  start(): void {
    this.lastMs = performance.now();
    const loop = (now: number): void => {
      if (this.disposed) return;
      this.raf = requestAnimationFrame(loop);
      const frameDt = Math.min(0.1, (now - this.lastMs) / 1000);
      this.lastMs = now;
      this.accumulator += frameDt;

      let steps = 0;
      while (this.accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
        const input = this.opts.inputManager.sample(FIXED_DT);
        if (!this.paused) {
          this.prevPos.copy(this.curPos);
          this.prevQuat.copy(this.curQuat);
          const events = this.sim.step(input);
          const snap = this.sim.snapshot(input);
          this.lastSnap = snap;
          this.curPos.set(snap.pos.x, snap.pos.y, snap.pos.z);
          this.curQuat.set(snap.quat.x, snap.quat.y, snap.quat.z, snap.quat.w);
          this.timeS = snap.tick * FIXED_DT;
          this.opts.onTick(snap, events);
        }
        this.accumulator -= FIXED_DT;
        steps++;
      }
      if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0;

      this.renderFrame(frameDt);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private renderFrame(dt: number): void {
    const alpha = Math.min(1, this.accumulator / FIXED_DT);
    this.tmpPos.lerpVectors(this.prevPos, this.curPos, alpha);
    this.tmpQuat.slerpQuaternions(this.prevQuat, this.curQuat, alpha);
    const snap = this.lastSnap;

    this.carVisual.update(this.tmpPos, this.tmpQuat, snap, this.timeS);

    // Conos sincronizados con la física
    const cones = this.sim.conePoses();
    for (let i = 0; i < cones.length && i < this.built.coneMeshes.length; i++) {
      const mesh = this.built.coneMeshes[i]!;
      const c = cones[i]!;
      mesh.position.set(c.pos.x, c.pos.y, c.pos.z);
      mesh.quaternion.set(c.quat.x, c.quat.y, c.quat.z, c.quat.w);
    }

    // Zona activa pulsante
    for (const [id, mesh] of this.built.zoneMeshes) {
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = id === this.activeZoneId ? 0.24 + 0.14 * Math.sin(this.timeS * 5) : 0.14;
    }

    // Cámara principal
    let mainCam: THREE.PerspectiveCamera;
    if (this.cameraMode === 'cockpit') {
      this.cockpit.update(this.tmpPos, this.tmpQuat, snap?.glance ?? 'none', dt);
      mainCam = this.cockpit.camera;
    } else {
      this.chase.update(this.tmpPos, this.tmpQuat, dt);
      mainCam = this.chase.camera;
    }

    this.renderer.render(this.built.scene, mainCam);

    // Vistas PiP: espejos al mirar y cámara al dar marcha atrás
    if (snap) {
      const d = this.opts.spec.dims;
      if (snap.glance === 'left' || snap.glance === 'right') {
        const side = snap.glance === 'left' ? -1 : 1;
        this.placePipCamera(
          this.pipMirror.camera,
          side * (d.widthM / 2 + 0.06),
          1.05,
          d.lengthM * 0.14,
          side * 0.3,
          -0.03,
        );
        this.pipMirror.render(this.renderer, this.built.scene, PIP_RECTS[snap.glance]);
      } else if (snap.glance === 'rear') {
        this.placePipCamera(this.pipRear.camera, 0, 1.3, 0.1, 0, 0);
        this.pipRear.render(this.renderer, this.built.scene, PIP_RECTS.rear);
      }
      if (snap.gear === 'R' && this.opts.spec.assists.reverseCam) {
        this.placePipCamera(this.pipReverse.camera, 0, 0.92, -(d.lengthM / 2) + 0.06, 0, -0.3);
        this.pipReverse.render(this.renderer, this.built.scene, PIP_RECTS.reverse);
      }
    }
  }

  /** Coloca una cámara PiP en coordenadas locales del coche mirando hacia atrás. */
  private placePipCamera(
    cam: THREE.PerspectiveCamera,
    lx: number,
    ly: number,
    lz: number,
    yaw: number,
    pitch: number,
  ): void {
    this.tmpOffset.set(lx, ly, lz).applyQuaternion(this.tmpQuat).add(this.tmpPos);
    cam.position.copy(this.tmpOffset);
    // La cámara mira -Z; con la orientación del coche eso ya es "hacia atrás"
    cam.quaternion.copy(this.tmpQuat);
    if (yaw !== 0) {
      this.tmpQ2.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
      cam.quaternion.multiply(this.tmpQ2);
    }
    if (pitch !== 0) {
      this.tmpQ2.setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitch);
      cam.quaternion.multiply(this.tmpQ2);
    }
  }

  setCameraMode(mode: CameraMode): void {
    this.cameraMode = mode;
  }

  setActiveZone(id: string | null): void {
    this.activeZoneId = id;
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    this.pipMirror.dispose();
    this.pipRear.dispose();
    this.pipReverse.dispose();
    this.built.dispose();
    this.sim.dispose();
    this.renderer.dispose();
  }
}
